import {asMoneyString} from './asMoneyString'
import {debounce} from './debounce'

const $window = $(window)

/**
 * I am a model of the Shopify shopping cart
 *
 * I have items, which you can modify. Upon modifying those items I'll update
 * the server so that the server view of the cart matches yours.
 */
export class Cart {
	constructor(items = []) {
		this.items = new Set()
		items.forEach(item => this.addItem(item, {notify: false}))
		this.serverView = this.getClientViewOfCart()
		this.currentAction = Promise.resolve()
		this.update = debounce(this.update.bind(this), 150)
	}

	/**
	 * Add an item to the cart
	 *
	 * If you have already added an item holding a matching variant then I'll
	 * merge the two and update the quantity.
	 *
	 * @param {CartItem} item
	 * @param {Object} opts
	 * @param {Boolean} opts.notify
	 */
	addItem(item, opts = {}) {
		const {notify = true} = opts
		const existingItem = Array.from(this.items).find(existingItem => {
			return existingItem.isEqualTo(item)
		})

		if (existingItem) {
			existingItem.absorb(item)
			if (notify) {
				$window.trigger('cart:onItemAbsorbed', item)
			}
			return
		}

		if (!this.items.has(item)) {
			this.items.add(item)
			item.setCart(this)
			if (notify) {
				$window.trigger('cart:onItemAdded', item)
			}
		}
	}

	removeItem(item, opts = {}) {
		const {update = true} = opts
		if (this.items.has(item)) {
			this.items.delete(item)
			if (update) {
				this.update()
			}
		}
	}

	update() {
		$window.trigger('cart:onUpdateStart', this)
		this.nextAction = this.sendUpdateRequestToServer.bind(this)
		this.currentAction.finally(() => {
			const action = this.nextAction
			this.nextAction = Promise.resolve()
			return action().then(() => {
				$window.trigger('cart:onUpdateEnd', this)
			})
		})

		return this.currentAction
	}

	sendUpdateRequestToServer() {
		if (this.isDirty()) {
			const cr = this.getChangeRecord()
			return jQuery.post('/cart/update', {
				updates: cr
			}).then(() => {
				this.serverView = this.getClientViewOfCart()
			})
		}

		return Promise.resolve()
	}

	findItemWithVariant({id}) {
		return Array.from(this.items.values()).find(item => {
			return item.getVariant().id === id
		})
	}

	getClientViewOfCart() {
		const items = Array.from(this.items.values())
		return items.reduce((memo, item) => {
			return Object.assign({}, memo, item.asJSON())
		}, {})
	}

	getChangeRecord() {
		const serverView = this.serverView
		const clientView = this.getClientViewOfCart()

		const cr = {}
		Object.keys(serverView).forEach(key => {
			cr[key] = 0
		})
		Object.keys(clientView).forEach(key => {
			let quantity = clientView[key]
			if (key in cr) {
				quantity += cr[key]
			}
			cr[key] = quantity
		})
		return cr
	}

	getSubTotalAsMoneyString() {
		const rawSubTotal = Array.from(this.items.values()).reduce((subtotal, item) => {
			return subtotal + item.getSubTotal()
		}, 0)
		return asMoneyString(rawSubTotal)
	}

	getQuantity() {
		return Array.from(this.items.values()).reduce((quantity, item) => {
			return quantity + item.getQuantity()
		}, 0)
	}

	isDirty() {
		const serverView = this.serverView
		const clientView = this.getClientViewOfCart()
		const isClientDifferentFromServer = Object.keys(clientView).reduce((differ, key) => {
			if (!(key in serverView)) {
				return true
			}
			if (serverView[key] !== clientView[key]) {
				return true
			}
			return differ
		}, false)
		const isServerDifferentFromClient = Object.keys(serverView).reduce((differ, key) => {
			if (!(key in clientView)) {
				return true
			}
			if (clientView[key] !== serverView[key]) {
				return true
			}
			return differ
		}, false)
		return isClientDifferentFromServer || isServerDifferentFromClient
	}

	isEmpty() {
		return this.items.size === 0
	}
}